<template>
  <div class="page-content px-3 pt-2">
    <div
      v-if="pending"
      class="text-center py-3"
    >
      <Loader />
    </div>
    <div
      v-else
      style="max-width: 720px"
    >
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="text-muted small pr-3">
          {{ $t('homeBanners.info') }}
        </div>
        <button
          class="btn btn-sm btn-primary text-nowrap"
          :disabled="saving"
          @click="add"
        >
          <i class="fas fa-plus mr-1" />
          {{ $t('homeBanners.add') }}
        </button>
      </div>

      <div
        v-if="error"
        class="alert alert-danger small"
      >
        {{ error }}
      </div>

      <div
        v-for="(banner, index) in banners"
        :key="index"
        class="banner-row d-flex align-items-center mb-2"
      >
        <div
          class="banner-preview d-flex flex-grow-1"
          :style="banner.backgroundColor ? { backgroundColor: banner.backgroundColor } : null"
        >
          <div class="d-flex flex-column flex-grow-1 p-3 overflow-hidden">
            <div class="banner-title">
              {{ banner.title }}
            </div>
            <div
              v-if="banner.description"
              class="banner-description small"
            >
              {{ banner.description }}
            </div>
            <div
              v-if="banner.url"
              class="mt-auto pt-2"
            >
              <span
                class="banner-button"
                :title="banner.url"
              >
                {{ banner.buttonText }}
              </span>
            </div>
          </div>
          <img
            v-if="banner.icon"
            :src="imageSrc(banner.icon)"
            class="banner-image"
          >
        </div>

        <div
          class="banner-meta small px-3"
          style="width: 200px"
        >
          <span
            class="badge"
            :class="`badge-${status(banner).variant}`"
          >
            {{ status(banner).label }}
          </span>
          <div class="text-muted mt-1">
            {{ dateRange(banner) }}
          </div>
          <div class="text-muted text-truncate">
            <i class="far fa-users mr-1" />
            {{ groupNames(banner) }}
          </div>
        </div>

        <div class="d-flex flex-column">
          <button
            class="btn btn-sm btn-link p-1"
            :disabled="saving || index === 0"
            @click="move(index, -1)"
          >
            <i class="fas fa-chevron-up" />
          </button>
          <button
            class="btn btn-sm btn-link p-1"
            :disabled="saving || index === banners.length - 1"
            @click="move(index, 1)"
          >
            <i class="fas fa-chevron-down" />
          </button>
        </div>
        <button
          class="btn btn-sm btn-outline-secondary ml-2"
          :disabled="saving"
          @click="edit(index)"
        >
          <i class="fas fa-pen" />
        </button>
        <button
          class="btn btn-sm btn-outline-danger ml-1"
          :disabled="saving"
          @click="removeIndex = index"
        >
          <i class="fas fa-trash" />
        </button>
      </div>

      <div
        v-if="banners.length === 0"
        class="text-muted text-center py-4"
      >
        {{ $t('homeBanners.empty') }}
      </div>
    </div>

    <BModal
      :visible="editIndex !== null"
      hide-footer
      size="lg"
      :title="editIndex === -1 ? $t('homeBanners.add') : $t('homeBanners.edit')"
      @hidden="editIndex = null"
    >
      <div
        v-if="error"
        class="alert alert-danger small"
      >
        {{ error }}
      </div>
      <HomeBannerForm
        v-if="editIndex !== null"
        :key="editIndex"
        :banner="editIndex >= 0 ? banners[editIndex] : null"
        @save="saveBanner"
        @cancel="editIndex = null"
      />
    </BModal>

    <BModal
      :visible="removeIndex !== null"
      hide-footer
      :title="$t('general.confirmRemove')"
      @hidden="removeIndex = null"
    >
      <p>{{ $t('homeBanners.removeConfirm') }}</p>
      <div class="text-right mt-3">
        <button
          class="btn btn-secondary mr-2"
          @click="removeIndex = null"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          class="btn btn-danger"
          @click="remove"
        >
          {{ $t('general.remove') }}
        </button>
      </div>
    </BModal>
  </div>
</template>

<script>
import HomeBannerForm from '@/components/messages/HomeBannerForm';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

const PROPERTY_NAME = 'HOME_BANNER';

export default {
  data: () => ({
    banners: [],
    pending: false,
    saving: false,
    error: null,
    editIndex: null,
    removeIndex: null,
  }),
  components: {
    HomeBannerForm,
  },
  computed: {
    ...mapGetters(['groups']),
  },
  methods: {
    ...mapActions(['getPayeeProperties', 'setPayeeProperties']),
    imageSrc(icon) {
      return icon.startsWith('data:') ? icon : `data:image/jpeg;base64,${icon}`;
    },
    status({ dateFrom, dateTo }) {
      const now = moment();
      if (dateTo && moment(dateTo).isBefore(now)) {
        return { label: this.$t('homeBanners.expired'), variant: 'secondary' };
      }
      if (dateFrom && moment(dateFrom).isAfter(now)) {
        return { label: this.$t('homeBanners.scheduled'), variant: 'info' };
      }
      return { label: this.$t('homeBanners.active'), variant: 'success' };
    },
    dateRange({ dateFrom, dateTo }) {
      if (!dateFrom && !dateTo) return this.$t('homeBanners.noTimeLimit');
      const format = (x) => (x ? moment(x).format('DD.MM.YYYY HH:mm') : '…');
      return `${format(dateFrom)} – ${format(dateTo)}`;
    },
    groupNames({ groupCodes }) {
      if (!groupCodes || groupCodes.length === 0) return this.$t('homeBanners.allPayers');
      return groupCodes
        .map((code) => (this.groups.find((x) => x.code === code) || { name: code }).name)
        .join(', ');
    },
    add() {
      this.editIndex = -1;
    },
    edit(index) {
      this.editIndex = index;
    },
    saveBanner(banner) {
      const banners = [...this.banners];
      if (this.editIndex === -1) {
        banners.push(banner);
      } else {
        banners.splice(this.editIndex, 1, banner);
      }

      this.persist(banners).then((saved) => {
        if (saved) this.editIndex = null;
      });
    },
    move(index, direction) {
      const banners = [...this.banners];
      const [banner] = banners.splice(index, 1);
      banners.splice(index + direction, 0, banner);
      this.persist(banners);
    },
    remove() {
      const banners = this.banners.filter((_, i) => i !== this.removeIndex);
      this.removeIndex = null;
      this.persist(banners);
    },
    persist(banners) {
      this.saving = true;
      this.error = null;

      return this.setPayeeProperties({
        data: {
          name: PROPERTY_NAME,
          value: banners.length > 0 ? JSON.stringify({ banners }) : '',
        },
      })
        .then(() => {
          this.banners = banners;
          return true;
        })
        .catch(() => {
          this.error = this.$t('homeBanners.saveError');
          return false;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    request() {
      this.pending = true;
      this.getPayeeProperties({
        params: {
          query: {
            names: PROPERTY_NAME,
          },
        },
      })
        .then(({ data }) => {
          const property = data.find((x) => x.key === PROPERTY_NAME);
          this.banners = property && property.value
            ? JSON.parse(property.value).banners || []
            : [];
        })
        .catch(() => {
          this.error = this.$t('homeBanners.loadError');
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.$emit('page', 'banners');
    this.request();
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/vars';

.banner-row {
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.banner-preview {
  min-height: 90px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #e5f0fe;
}

.banner-title {
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.banner-button {
  display: inline-block;
  padding: 0.2rem 0.9rem;
  border-radius: 999px;
  background-color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
}

.banner-description {
  color: #555;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.banner-image {
  width: 110px;
  object-fit: cover;
}
</style>
